import { h } from 'preact';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'preact/hooks';
import { forwardRef } from 'preact/compat';
import { Angle } from 'utils/icon/icons';

const GiftCardForm = forwardRef((props, ref) => {
  const [focused, setFocused] = useState(false);
  const [errors, setErrors] = useState({});
  const dateInput = useRef(null);
  const [giftFormState, setGiftFormState] = useState({
    _recipient_name: '',
    _recipient_email: '',
    _sender_name: '',
    _greeting: '',
    _ecert_delivery_date: '',
    _is_gift_card: 'true',
  });

  const validate  = useCallback(() => {
    const formErrors = {};
    // eslint-disable-next-line no-useless-escape
    const isValidEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;
    ['_recipient_name', '_ecert_delivery_date', '_sender_name'].forEach((field) => {
      if (!giftFormState[field]?.length) {
        formErrors[field] = window.TMW.Strings.giftCard.fieldError;
      }
    });
    if (!giftFormState._recipient_email.match(isValidEmail)) {
      formErrors._recipient_email = window.TMW.Strings.giftCard.emailError;
    }
    return formErrors;
  }, [giftFormState]);

  useImperativeHandle(ref, () => ({
    getFormState() {
      const formErrors = validate();
      setErrors({...formErrors});
      if (!Object.keys(formErrors).length) {
        return giftFormState;
      }
      return null;
    }
  }));

  useEffect(() => {
    if (focused) {
      dateInput.current?.showPicker();
    }
  }, [focused]);

  useEffect(() => {
    if (Object.keys(errors).length) {
      setErrors({...validate()});
    }
  }, [giftFormState]);

  const handleFocus = (e) => {
    setFocused(true);
    if (e.target.type === 'date') {
      e.target.showPicker();
    }
  };

  const minDate = useMemo(() => {
    const [date] = new Date().toISOString().split('T');
    return date;
  }, []);

  return (
    <div
      data-cardigan-product-form="form"
      className="mt-5 pt-5 border-t border-grey-4"
    >
      <div className='mb-5'>
        <input
          className={`w-full px-5 pt-[13px] pb-[15px] text-body-2 
          focus:outline-none bg-white text-black 
          text-body-2 placeholder:text-grey-1
          ${errors._recipient_name ? 'border-red' : 'border-grey-2'} `}
          data-cardigan-product-form="recipient-name"
          type="text"
          value={giftFormState._recipient_name}
          required
          placeholder={window.TMW.Strings.giftCard.recipientName}
          onChange={(e) =>
            setGiftFormState({
              ...giftFormState,
              _recipient_name: e.target.value,
            })
          }
        />
        {!!errors._recipient_name && (
          <p className="block text-right text-red text-body-2">
            {errors._recipient_name}
          </p>
        )}
      </div>

      <div className="mb-5">
        <input
          className={`w-full px-5 pt-[13px] pb-[15px] text-body-2 
            focus:outline-none bg-white  text-black 
            text-body-2 placeholder:text-grey-1
          ${errors._recipient_email ? 'border-red' : 'border-grey-2'} `}
          data-cardigan-product-form="recipient-email"
          type="email"
          value={giftFormState._recipient_email}
          required
          placeholder={window.TMW.Strings.giftCard.recipientEmail}
          onChange={(e) =>
            setGiftFormState({
              ...giftFormState,
              _recipient_email: e.target.value,
            })
          }
        />
        {!!errors._recipient_email && (
          <p className="block text-right text-red text-body-2">
            {errors._recipient_email}
          </p>
        )}
      </div>

      <div className='mb-5'>
        <input
          className={`w-full px-5 pt-[13px] pb-[15px] text-body-2 
            focus:outline-none bg-white text-black 
            text-body-2 placeholder:text-grey-1
            ${errors._sender_name ? 'border-red' : 'border-grey-2'} `}
          data-cardigan-product-form="sender-name"
          type="text"
          value={giftFormState._sender_name}
          placeholder={window.TMW.Strings.giftCard.senderName}
          onChange={(e) =>
            setGiftFormState({ ...giftFormState, _sender_name: e.target.value })
          }
        />
        {!!errors._sender_name && (
          <p className="block text-right text-red text-body-2">
            {errors._sender_name}
          </p>
        )}
      </div>

      <div className="flex flex-col mb-5">
        <textarea
          className="w-full px-5 pt-[13px] pb-[15px] text-body-2 
            focus:outline-none bg-white border-grey-2 text-black 
            text-body-2 placeholder:text-grey-1 placeholder:text-body-2"
          data-cardigan-product-form="greeting"
          maxLength="200"
          rows="5"
          cols="25"
          value={giftFormState._greeting}
          placeholder={window.TMW.Strings.giftCard.greeting}
          onChange={(e) =>
            setGiftFormState({ ...giftFormState, _greeting: e.target.value })
          }
        />
        <span className="block text-right text-body-2 text-grey-1">200</span>
      </div>

      <div className='mb-5'>
        <label htmlFor='date_picker_input' className={'cursor-pointer text-lg'}>Delivery Date</label>
        <span
          className={`relative h-[54px] pt-[13px] pb-[15px] cursor-pointer`}
        >
          <input
            id={'date_picker_input'}
            className={`group w-full h-[54px] pl-5 pr-4 text-body-2 
              cursor-pointer bg-white border-grey-2 mt-1
              focus:outline-none text-black placeholder:text-grey-1
            ${errors._ecert_delivery_date ? 'border-red' : 'border-grey-2'}`}
            data-cardigan-product-form="delivery-date"
            ref={dateInput}
            min={minDate}
            type={'date'}
            value={giftFormState._ecert_delivery_date}
            placeholder={window.TMW.Strings.giftCard.deliveryDate}
            onFocus={handleFocus}
            onBlur={(e) => {
              setGiftFormState({
                ...giftFormState,
                _ecert_delivery_date: e.target.value,
              });
            }}
          />
          {!focused && (
            <div className={`rotate-180 absolute right-[22px] top-4`}>
              <Angle />
            </div>
          )}
        </span>
        {!!errors._ecert_delivery_date && (
          <p className="block text-right text-red text-body-2">
            {errors._ecert_delivery_date}
          </p>
        )}
      </div>
    </div>
  );
});

export default GiftCardForm;
